// The browser host for the X-ray factory's loop.
//
// `runLoop` was written with a hole in it: the caller supplies `ask(request)` and
// owns transport, so the package stays dependency-free and the judge stage is
// testable offline. Nothing in the repo filled that hole — the judge has never
// been run against a model. This fills it with the same in-browser sub-agent path
// X-ray Describe and Workflows already use, which means the raw commands never
// leave the tenant: they go to the tenant's own configured model through the LLM
// proxy, not to a corpus export.
//
// Redaction still applies. `buildJudgeRequest` redacts home-directory paths by
// default and that default is kept — the evidence is raw commands, so it carries
// operator identities whatever the transport.

import { Type } from "@earendil-works/pi-ai";
import { runSubAgent } from "./subAgent.js";
import { buildJudgeRequest, validateVerdicts, probeSupport } from "../../xray-factory/src/judge/index.js";
import { auditOps } from "../../xray-factory/src/audit/index.js";
import { buildRefineRequest, validateRefinement } from "./xrayRuntimeLensRefine.js";
import { buildArtifactDraftRequest, validateArtifactDraft } from "./xrayArtifactDraft.js";
import { redactPaths } from "../../xray-factory/src/mine/skeleton.js";
import { createPythonTool } from "./pythonTool.js";

// One re-ask when the verdict set fails validation. This is not repair — the
// verdicts are rejected and the model is shown its own validation errors, the
// same shape as subAgent's RETURN_NUDGE. Repairing a verdict set in code would
// put the host in the business of deciding what the judge meant.
const MAX_JUDGE_RETRIES = 1;

/**
 * An `ask` for runLoop, backed by a sub-agent.
 *
 * Returns the parsed object, or throws. runLoop catches and reports the stage.
 */
export function askViaSubAgent({ model = null, signal = null, onEvent = null, tools = [] } = {}) {
  return async function ask(request) {
    const r = await runSubAgent({
      role: "task",
      model,
      signal,
      tools,
      systemPrompt: request.instructions,
      outputSchema: request.schema,
      task: request.prompt,
      onEvent: onEvent || undefined,
    });
    if (r.status !== "done") {
      throw new Error(r.error?.message || `judge ${r.status}`);
    }
    if (!r.result || typeof r.result !== "object") {
      throw new Error("the judge returned no structured verdict set");
    }
    return r.result;
  };
}

// ── Generation-time freedom. The judge and the artifact drafter get TOOLS:
// sandboxed python over the evidence, and the evidence itself on demand. The
// contract binds emissions at read time; nothing binds how hard an agent may
// look before committing to one.

/**
 * The evidence tool: kept raw instances of any cluster in the batch, so a
 * generating agent can read past the payload's sample instead of extrapolating
 * from it — the same anti-extrapolation move the slot profiles make, but on
 * demand.
 */
export function makeReadInstancesTool(clusters) {
  const byKey = new Map((clusters || []).map((c) => [c.key, c]));
  return {
    name: "read_cluster_instances",
    label: "Read cluster instances",
    description:
      "Read the kept raw instances (command + output) of one cluster in this batch, past the sample "
      + "already shown. Use before committing to a claim scope or a probe: five samples cannot tell "
      + "you how a shape varies.",
    parameters: Type.Object({
      key: Type.String({ description: "The cluster key, exactly as given in the payload." }),
      offset: Type.Optional(Type.Number({ description: "Skip this many instances (default 0)." })),
    }),
    async execute(toolCallId, args) {
      const c = byKey.get(String(args?.key || ""));
      if (!c) {
        return {
          content: [{ type: "text", text: `error: unknown cluster key "${args?.key}". Keys in this batch: ${[...byKey.keys()].join(", ")}` }],
          isError: true,
        };
      }
      const offset = Math.max(0, Number(args?.offset) || 0);
      const slice = (c.instances || []).slice(offset, offset + 8);
      if (!slice.length) {
        return { content: [{ type: "text", text: `no instances at offset ${offset} — the cluster keeps ${(c.instances || []).length}` }] };
      }
      const text = slice
        .map((inst, i) => `[${offset + i}] sent: ${redactPaths(String(inst?.command || ""))}\n    got: ${inst?.output ? redactPaths(String(inst.output)).slice(0, 900) : `(${inst?.outcome === "errored" ? "errored, no output" : "no result captured"})`}`)
        .join("\n\n");
      return { content: [{ type: "text", text: `${slice.length} of ${(c.instances || []).length} kept instances:\n\n${text}` }] };
    },
  };
}

/**
 * The AUTHORITATIVE pattern tester: compiles and runs a candidate probe with
 * the exact engine and flags the lens will use — `new RegExp(pattern, "i")`,
 * 200-char cap. This tool exists because of a real failure: the judge
 * stress-tested its regexes in python, where `(?s)` is legal, and every
 * verdict shipped patterns JavaScript refused. Python is for exploration;
 * this is for the patterns that get returned.
 */
export function makeTestPatternTool(clusters) {
  const all = clusters || [];
  return {
    name: "test_pattern",
    label: "Test a probe pattern",
    description:
      "Compile and run a `where` or check pattern EXACTLY as the lens will — JavaScript "
      + "`new RegExp(pattern, \"i\")`, max 200 chars — against the batch's kept instances. "
      + "This is the authoritative dialect: python regex is close but not identical "
      + "(no inline flags like (?s)/(?i), no (?P<name>), no \\A/\\Z). Every pattern you return "
      + "in a verdict MUST have passed through this tool.",
    parameters: Type.Object({
      pattern: Type.String({ description: "The regex source, without delimiters or flags." }),
      on: Type.Optional(Type.String({ description: "What it reads: `command` (default) or `output`." })),
      key: Type.Optional(Type.String({ description: "Limit to one cluster key; default tests every cluster in the batch." })),
    }),
    async execute(toolCallId, args) {
      const src = String(args?.pattern ?? "");
      const on = args?.on === "output" ? "output" : "command";
      if (src.length > 200) {
        return { content: [{ type: "text", text: `INVALID: pattern is ${src.length} chars — the lens caps patterns at 200` }], isError: true };
      }
      try {
        new RegExp(src, "i");
      } catch (err) {
        return {
          content: [{
            type: "text",
            text: `INVALID in the lens's engine (JavaScript): ${err.message}\n`
              + "Common dialect traps: inline flags (?s)/(?i)/(?m) do not exist (the lens already applies \"i\"; "
              + "for dot-matches-newline use [\\s\\S]), (?P<name>) is (?<name>), \\A/\\Z are ^/$.",
          }],
          isError: true,
        };
      }
      const subset = args?.key ? all.filter((c) => c.key === args.key) : all;
      if (!subset.length) {
        return { content: [{ type: "text", text: `error: no cluster ${args?.key ? `with key "${args.key}"` : "in the batch"}` }], isError: true };
      }
      const lines = subset.map((c) => {
        const s = probeSupport(c, { on, pattern: src });
        return `${c.key}: ${s.matched}/${s.total} kept instances match on ${on}`;
      });
      return { content: [{ type: "text", text: `VALID (js) — measured support:\n${lines.join("\n")}` }] };
    },
  };
}

/**
 * Corpus-wide search across EVERY cluster's kept instances, by command or by
 * output. The tool that makes cross-shape behaviours findable at all: the
 * clustering is syntactic, so an outcome that recurs under sed, grep and cargo
 * appears as three unrelated fragments and no per-cluster reader can see the
 * pattern. This returns the spread — which keys carry it, and how often.
 */
export function makeSearchOutputsTool(clusters) {
  const all = clusters || [];
  return {
    name: "search_outputs",
    label: "Search the batch's evidence",
    description:
      "Search EVERY cluster's kept instances at once, by `output` (default) or `command`. Use when a "
      + "result looks like a recurring failure mode rather than one cluster's business — a path that "
      + "did not resolve, an expired credential, a rate limit. Returns the per-cluster spread plus "
      + "matching excerpts, so a behaviour that spans shapes stops being invisible.",
    parameters: Type.Object({
      pattern: Type.String({ description: "Case-insensitive regex, <=200 chars — the same engine the lens uses." }),
      on: Type.Optional(Type.String({ description: "`output` (default) or `command`." })),
    }),
    async execute(toolCallId, args) {
      const src = String(args?.pattern ?? "");
      const on = args?.on === "command" ? "command" : "output";
      if (src.length > 200) {
        return { content: [{ type: "text", text: "INVALID: pattern longer than 200 chars" }], isError: true };
      }
      let re;
      try {
        re = new RegExp(src, "i");
      } catch (err) {
        return { content: [{ type: "text", text: `INVALID in the lens's engine (JavaScript): ${err.message}` }], isError: true };
      }
      const lines = [];
      const excerpts = [];
      let matched = 0;
      let total = 0;
      for (const c of all) {
        const insts = c.instances || [];
        total += insts.length;
        const hits = insts.filter((i) => re.test(String((on === "command" ? i?.command : i?.output) || "")));
        if (!hits.length) continue;
        matched += hits.length;
        lines.push(`  ${c.key} (${c.skeleton?.slice(0, 70) || "?"}): ${hits.length}/${insts.length}`);
        for (const h of hits.slice(0, 2)) {
          if (excerpts.length >= 6) break;
          excerpts.push(`  [${c.key}] ${redactPaths(String(h.command || "")).slice(0, 160)}\n      → ${redactPaths(String(h.output || "")).replace(/\s+/g, " ").slice(0, 200)}`);
        }
      }
      if (!matched) {
        return { content: [{ type: "text", text: `no match on ${on} across ${total} kept instances in ${all.length} clusters` }] };
      }
      return {
        content: [{
          type: "text",
          text: `${matched} of ${total} kept instances match on ${on}, across ${lines.length} of ${all.length} clusters:\n`
            + `${lines.join("\n")}\n\nexcerpts:\n${excerpts.join("\n")}`
            + (lines.length > 1
              ? "\n\nSpanning several clusters means this is an OUTCOME-defined behaviour: name it for the outcome and give outcomePattern."
              : ""),
        }],
      };
    },
  };
}

/** The generation-time toolset: sandboxed python, the evidence reader, and the authoritative pattern tester. */
export function generationTools(clusters) {
  return [createPythonTool(), makeReadInstancesTool(clusters), makeTestPatternTool(clusters), makeSearchOutputsTool(clusters)];
}

// Appended to the factory's transport-agnostic instructions by THIS host,
// because the tools are the host's: the factory package stays dependency-free
// and cannot promise a python realm it does not own.
export const GENERATION_TOOLS_APPENDIX = `

## Your tools, and the obligation they create

You have \`execute_python\` (an isolated interpreter; pass evidence via
args.data), \`read_cluster_instances\` (the kept raw instances of any cluster,
past the sample above), \`test_pattern\` (compiles and measures a probe with the
EXACT engine the lens will use), and \`search_outputs\` (searches EVERY cluster's
evidence at once — the only way to see a behaviour that spans shapes).

Freedom at generation time carries one obligation:
STRESS-TEST BEFORE YOU COMMIT. And the dialect matters — probes execute as JavaScript
\`new RegExp(pattern, "i")\`, not python \`re\`: inline flags like (?s)/(?i) do
not exist there (use [\\s\\S] for dot-matches-newline; "i" is already applied),
(?P<name>) is (?<name>), \\A/\\Z are ^/$. Python is for exploring the evidence;
\`test_pattern\` is authoritative. EVERY \`where\` and EVERY check pattern you
return must have passed test_pattern with support you have read — a verdict
carrying a pattern this tool has not validated will be rejected, and a probe
you did not execute is a guess wearing a regex.`;

/**
 * Judge a batch of clusters, re-asking once on a validation failure.
 *
 * @returns {{ok, problems, verdicts, ranking, attempts, request}}
 *   The validation result IS the artifact — the loop's gate reads `problems`, so
 *   an invalid set blocks with the field that was missing rather than a generic
 *   failure.
 */
export async function judgeClusters(clusters, {
  covered = [],
  budget = 5,
  instances = 5,
  model = null,
  signal = null,
} = {}) {
  const request = buildJudgeRequest(clusters, { budget, instances, covered });
  // Generation is free: the judge can execute python against the evidence and
  // read past the payload's sample before committing to a verdict set.
  const ask = askViaSubAgent({ model, signal, tools: generationTools(clusters) });
  request.instructions += GENERATION_TOOLS_APPENDIX;
  const byKey = new Map(clusters.map((c) => [c.key, c]));

  let response = null;
  let check = null;
  let attempts = 0;
  let prompt = request.prompt;

  while (attempts <= MAX_JUDGE_RETRIES) {
    attempts += 1;
    response = await ask({ stage: "judge", ...request, prompt });
    check = validateVerdicts(response, request.keys, { clusters: byKey });
    if (check.ok) break;
    if (attempts > MAX_JUDGE_RETRIES) break;
    prompt = `${request.prompt}\n\nYour previous response was rejected:\n`
      + check.problems.map((p) => `  - ${p}`).join("\n")
      + "\n\nProduce the whole verdict set again, corrected. Do not drop clusters to avoid a problem —"
      + " every key must be judged.";
  }

  return {
    ...check,
    verdicts: response?.verdicts || [],
    ranking: response?.ranking || [],
    attempts,
    request,
  };
}

/**
 * Re-run one existing lens over the corpus and report where its grammar
 * mis-reads. This is the loop's DEFAULT entry, not `build`: a grammar induced
 * from one window rots as the corpus changes and nothing else notices.
 *
 * @param {object} spec    from registry.auditSpecs() — {id, extract, collect, groupBy, anchor, ignore}
 * @param {object[]} bodies  {promptId, sessionId, endpointId, endpointName, ts, parsed}
 */
export function auditLensOverBodies(spec, bodies) {
  const { flat, producing } = runLensOverBodies(spec, bodies);

  const report = auditOps(flat, {
    groupBy: spec.groupBy || undefined,
    anchor: spec.anchor || undefined,
    ignore: spec.ignore || [],
  });

  const total = (bodies || []).length;
  return {
    lens: spec.id,
    ops: flat.length,
    // Corpus yield: what fraction of bodies this lens read anything out of.
    // The audit gate refuses under 10% — a yield collapse is how a lens dies
    // quietly, with the grammar still parsing and the substrate moved.
    yieldPct: total ? Math.round((100 * producing) / total) : null,
    findings: report?.findings || [],
  };
}

/**
 * Run a lens over bodies and return its flat ops.
 *
 * Shared by the audit stage and the reading below, because a reading produced by a
 * different traversal than the one that was audited is a reading of something else.
 */
function runLensOverBodies(spec, bodies) {
  const promptEvents = [];
  let producing = 0;
  let threw = 0;

  for (const b of bodies || []) {
    let ops = [];
    try {
      ops = spec.extract(b?.parsed?.turns || []) || [];
    } catch {
      // Counted, not just swallowed. A lens that throws on every body produced the
      // same "0 operations" as one that matched nothing, so a broken extractor read
      // as an honest absence — the claim this whole surface is built to avoid making.
      threw += 1;
      ops = [];
    }
    if (ops.length) producing += 1;
    promptEvents.push({
      promptId: b.promptId,
      sessionId: b.sessionId,
      endpointId: b.endpointId || null,
      endpointName: b.endpointName || null,
      ts: b.ts || null,
      ops,
    });
  }

  let flat = [];
  try {
    flat = spec.collect ? spec.collect(promptEvents) : promptEvents.flatMap((p) => p.ops);
  } catch {
    flat = promptEvents.flatMap((p) => p.ops);
  }
  return { flat, producing, threw, promptEvents };
}

/**
 * What a mounted lens actually READS out of the corpus, in the shape its panel wants.
 *
 * A lens judged trace-only gets no tab, which is the correct call and also meant a
 * freshly mounted lens rendered nowhere a reader could find: it appears inline in
 * some future trace's feed and nowhere else, so mounting one looked like it had done
 * nothing. This runs it against the window the factory already sampled and hands
 * back exactly what the tab would have shown — the same `group`/`structure` output
 * the host feeds the same panels — so a candidate can be judged on its reading
 * rather than on its spec.
 *
 * `ops === 0` is a real answer and is reported as one. A lens that reads nothing out
 * of the corpus it was induced from is the cheapest possible refutation, and it is
 * the outcome an empty panel must not be allowed to look like a loading state.
 */
export function readLensOverBodies(manifest, bodies) {
  const { flat, producing, threw } = runLensOverBodies(manifest, bodies);
  const total = (bodies || []).length;
  return {
    ops: flat.length,
    bodies: total,
    producing,
    // How many bodies the extractor threw on. Zero ops with a nonzero `threw` is a
    // broken lens; zero ops with `threw: 0` is a window that genuinely has none of
    // this work, and the two must not print the same sentence.
    threw,
    yieldPct: total ? Math.round((100 * producing) / total) : null,
    // Both shapes are computed because which one renders is the lens's own
    // declaration, and a lens edited from trace-only to structured must not need a
    // second call site to become visible.
    built: manifest.group ? manifest.group(flat) : null,
    structure: manifest.structure ? manifest.structure(flat) : null,
  };
}

/**
 * Locate a drafted lens's fields by reading its cluster's raw instances.
 *
 * One model call, one decision: where each field the judge named lives. No retry
 * loop — unlike the judge's verdict set, a partial answer here is USABLE (the
 * located fields work, the rest are reported absent), so re-asking would spend a
 * call to improve something already honest.
 */
export async function refineRuntimeLens(spec, cluster, { wanted = [], model = null, signal = null } = {}) {
  const request = buildRefineRequest(spec, cluster, { wanted });
  const ask = askViaSubAgent({ model, signal });
  const response = await ask({ stage: "refine", ...request });
  return validateRefinement(response, spec, { asked: request.asked });
}

/**
 * Draft a behaviour ARTIFACT from a judged verdict: an agent with python
 * writes emit(window) + a contract manifest, stress-testing against sampled
 * windows before returning. One re-ask when the acceptance check refuses,
 * with the problems shown — same posture as the judge's own retry.
 *
 * @returns {{ok, problems: string[], artifact: object|null, attempts: number}}
 */
export async function draftBehaviourArtifact(verdict, cluster, windows, { model = null, signal = null } = {}) {
  const request = buildArtifactDraftRequest(verdict, cluster, windows);
  const ask = askViaSubAgent({ model, signal, tools: generationTools(cluster ? [cluster] : []) });

  let check = { ok: false, problems: ["the agent returned nothing"], artifact: null };
  let attempts = 0;
  let prompt = request.prompt;
  while (attempts <= 1) {
    attempts += 1;
    const response = await ask({ stage: "artifact", instructions: request.instructions, schema: request.schema, prompt });
    check = validateArtifactDraft(response, verdict);
    if (check.ok) break;
    prompt = `${request.prompt}\n\nYour previous artifact was rejected:\n`
      + check.problems.map((p) => `  - ${p}`).join("\n")
      + "\n\nReturn the whole artifact again, corrected — source AND manifest.";
  }
  return { ...check, attempts };
}
